import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createEquipment, editEquipment, EQUIPMENT_QUERY_KEY } from './CarrierProfile.service'
import { type Equipment, type EquipmentFormValues, EquipmentType, FuelType, WeightUnit } from './CarrierProfile.types'

interface CreateOrEditEquipmentDialogPayload {
  readonly equipment: Equipment
}

export interface CreateOrEditEquipmentDialogProps {
  readonly payload?: CreateOrEditEquipmentDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const CreateOrEditEquipmentDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditEquipmentDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<EquipmentFormValues>()
  const queryClient = useQueryClient()
  const createEquipmentMutation = useMutation({
    mutationFn: async (data: EquipmentFormValues) => {
      await createEquipment(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EQUIPMENT_QUERY_KEY] }),
  })
  const updateEquipmentMutation = useMutation({
    mutationFn: async (data: EquipmentFormValues) => {
      await editEquipment(payload?.equipment.id ?? '', data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EQUIPMENT_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          weightUnit: payload?.equipment.weightUnit,
          type: payload?.equipment.type,
          weightCapacity: payload?.equipment.weightCapacity,
          fuelType: payload?.equipment.fuelType,
          quantity: payload?.equipment.quantity,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<EquipmentFormValues> = async (data: EquipmentFormValues): Promise<void> => {
    await handleFormSubmission(
      () => payload ? updateEquipmentMutation.mutateAsync(data) : createEquipmentMutation.mutateAsync(data),
      {
        onError: (error) => setError('root', processServerErrorMessages(error)),
        onSuccess: () => {
          analyticsEvent(`polestar_carrier_profile_equipment_${payload ? 'updated' : 'created'}`)
          onAction()
        },
      },
    )
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={payload ? t('screens.cs.carrier_profile.equipment.edit_equipment') : t('screens.cs.carrier_profile.equipment.add_equipment')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
        >
          {t('general.save')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormDropdown
          name="type"
          label={t('screens.cs.carrier_profile.equipment.form.equipment_type')}
          control={control}
          options={Object.values(EquipmentType).map((equipment) => ({
            value: equipment,
            label: t(`screens.cs.carrier_profile.equipment.equipment_enum.${equipment}`),
          }))}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <Stack direction="row" spacing={2}>
          <KNFormText
            name="weightCapacity"
            type="number"
            label={t('screens.cs.carrier_profile.equipment.form.weight_capacity')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
          <KNFormDropdown
            name="weightUnit"
            label={t('screens.cs.carrier_profile.equipment.form.weight_unit')}
            control={control}
            options={Object.values(WeightUnit).map((unit) => ({
              value: unit,
              label: t(`screens.cs.carrier_profile.equipment.measurement_enum.${unit}`),
            }))}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Stack>
        <KNFormDropdown
          name="fuelType"
          label={t('screens.cs.carrier_profile.equipment.form.fuel_type')}
          control={control}
          options={Object.values(FuelType).map((fuel) => ({
            value: fuel,
            label: t(`screens.cs.carrier_profile.equipment.fuel_enum.${fuel}`),
          }))}
          rules={{
            required: t('form.validation.required'),
          }}
        />
        <KNFormText
          name="quantity"
          type="number"
          label={t('screens.cs.carrier_profile.equipment.form.quantity')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
        />
      </Stack>
    </KNDialog>
  )
}

export default CreateOrEditEquipmentDialog
